import { get } from 'lodash';
import { FEDERATED_APP_SETTINGS } from 'common/constants/constants';
import { loadRemoteApp, resolveRemoteUrl } from 'common/utils/moduleFederation';
import ErrorBoundary from 'components/ErrorBoundary';
import { CenterLoader } from 'connected-components/Loader/Loader';
import { ComponentType, Suspense, lazy } from 'react';
import { DevCoachingAppProps } from 'types/micro-frontends';
import { useDispatch, useSelector } from 'react-redux';
import {
  dateRangeSelector,
  selectedPeriodKeySelector
} from 'state/selectors/ui-state';
import { prevIterationSelector } from 'state/selectors/iterations-picker';
import { getPrevDateRange } from 'components/PeriodPicker/periods/utils';
import {
  teamsFlatTreeSelector,
  selectedTeamWithExtraDataSelector
} from 'state/selectors/teams';
import useMetricsCalculationConfig from '../../hooks/useMetricsCalculationConfig';
import useBenchmarkOrgSizeConfig from 'views/root/views/home/hooks/useBenchmarkOrgSizeConfig';
import { organizationSelector } from 'state/selectors/organization';
import { useHistory } from 'react-router';
import useActiveContributors from 'components/hooks/useActiveContributors';
import EditUsersModal from 'modals/EditUsersModal/EditUserModal';
import useHandleSubmitMerge from 'components/ContributorManagementMenu/useHandleSubmitMerge';
import { rawContributorsWithSensorAuthSelector } from 'state/selectors/contributors';
import { pmIntegrationSelector } from 'state/selectors/sensors-authorizations';
import { SettingValues } from '@linearb/linjs-common';
import useSettingsService from 'components/hooks/useSettingsService';
import { repositoriesByIdSelector } from 'state/selectors/repositories';
import { getContributors } from 'state/callers';
import { myAccountSelector } from 'state/selectors/account';

const app = FEDERATED_APP_SETTINGS.DEV_COACHING;

const url = resolveRemoteUrl(process.env.NODE_ENV, window.location, app, true);

const DevCoachingApp = lazy<ComponentType<DevCoachingAppProps>>(
  loadRemoteApp(url, app)
);

export function DevCoaching() {
  const { getSettingOrDefinitionValue } = useSettingsService();
  const dispatch = useDispatch();

  const history = useHistory();
  // in the future we can pass ui-state instead of the current props
  const dateRange = useSelector(dateRangeSelector);
  const currentTeam = useSelector(selectedTeamWithExtraDataSelector)?.[0];
  const allTeams = useSelector(teamsFlatTreeSelector);
  const selectedPeriodKey = useSelector(selectedPeriodKeySelector);
  const prevIteration = useSelector(prevIterationSelector);
  const allContributorsWithSensor = useSelector(
    rawContributorsWithSensorAuthSelector
  );
  const organization = useSelector(organizationSelector);
  const account = useSelector(myAccountSelector);
  const { handleSubmitMerge } = useHandleSubmitMerge();
  const pmSensor = useSelector(pmIntegrationSelector) || {};

  const prevDateRange = getPrevDateRange({
    dateRange,
    selectedPeriodKey,
    prevIteration
  });
  const { metricsCalculationStrategy } = useMetricsCalculationConfig();
  const { benchmarkOrgSize } = useBenchmarkOrgSizeConfig();

  const issueInProgressTransitionStrategy =
    getSettingOrDefinitionValue({
      name: SettingValues.ISSUE_IN_PROGRESS_TRANSITION_STRATEGY
    }) || {};

  const codingTimeStrategy = get(
    issueInProgressTransitionStrategy,
    'key',
    'take_first'
  );

  const activeContributors = useActiveContributors();
  const repositoriesById = useSelector(repositoriesByIdSelector);

  const getUpdatedContributors = () => {
    getContributors()(dispatch);
  };

  return (
    <ErrorBoundary>
      <Suspense fallback={<CenterLoader />}>
        <DevCoachingApp
          dateRange={dateRange}
          prevDateRange={prevDateRange}
          currentTeam={currentTeam}
          contributors={activeContributors}
          allTeams={allTeams}
          metricsCalculationStrategy={metricsCalculationStrategy}
          organization={organization}
          history={history}
          allContributorsWithSensor={allContributorsWithSensor}
          EditUsersModal={EditUsersModal}
          editUsersModalCallBack={handleSubmitMerge}
          pmSensor={pmSensor}
          codingTimeStrategy={codingTimeStrategy}
          repositoriesById={repositoriesById}
          getUpdatedContributors={getUpdatedContributors}
          benchmarkOrgSize={benchmarkOrgSize}
          account={account}
        />
      </Suspense>
    </ErrorBoundary>
  );
}
